import { ErrorModal, TreeNode, TreePickerControlV2 } from "@dexteel/mesf-core";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import { DelayCode, TreeCode } from "../model/DelayCode";
import { getDelayCodes } from "../repositories/DelayCodeRepository";

export interface NodeTreeCodes {
  id: number;
  isActive: boolean;
  isLeaf: boolean;
  name: string;
  parentId: number | null;
  children: NodeTreeCodes[] | null;
}

const buildTreeCodes = (nodes: NodeTreeCodes[]): TreeNode => {
  //tree build functions
  const idMapping = nodes.reduce((acc: any, el: NodeTreeCodes, i: number) => {
    acc[el.id] = i;
    return acc;
  }, {});
  let root;
  nodes.forEach((el: NodeTreeCodes) => {
    // Handle the root element
    if (el.parentId === null) {
      root = el;
      return;
    }
    // Use our mapping to locate the parent element in our data array
    const parentEl = nodes[idMapping[el.parentId]];
    // Add our current el to its parent's `children` array
    if (parentEl) {
      parentEl.children = [...(parentEl?.children || []), el];
    }
  });
  // @ts-ignore
  return root;
};

export const DelayCodeTreePicker = ({
  label = "Code",
  value,
  onChange,
  disabled,
  assetId,
  fieldError,
}: {
  label?: String;
  value: number | null;
  onChange: (value: number | null, defaultCategoryId: number | null) => void;
  disabled?: boolean;
  assetId: number | null;
  fieldError?: FieldError | undefined;
}) => {
  const [rows, setRows] = useState<DelayCode[]>([]);
  const [delayCodes, setDelayCodes] = useState<TreeNode | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [getError, setGetError] = useState("");

  const loadData = async () => {
    setIsLoading(true);
    const response = await getDelayCodes(assetId);
    if (response.ok) {
      const rows = get(response, "data.tables[0].rows", []);
      setRows(rows);
      const rowsMapped = rows.map(
        (del: DelayCode) =>
          ({
            id: del.DelayCodeId,
            parentId: del.ParentDelayCodeId,
            name: del.DelayCodeName,
            isLeaf: del.IsLeaf,
            isActive: true,
            children: [],
          }) as TreeCode,
      );
      const treeCode = buildTreeCodes(rowsMapped);
      setDelayCodes(treeCode);
    } else {
      setGetError("Failed to fetch delay codes.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (assetId) {
      loadData();
    }
  }, [assetId]);

  if (isLoading || delayCodes == null) {
    return (
      <TextField
        disabled
        label="Code"
        variant="outlined"
        fullWidth
        size="small"
        slotProps={{
          input: {
            endAdornment: <CircularProgress color="inherit" size={20} />,
          },
        }}
      />
    );
  } else {
    return (
      <>
        <Box
          sx={{
            "& .MuiGrid-root.MuiGrid-container": {
              "& .MuiGrid-item": {
                "& .MuiFormControl-root": {
                  "& .MuiInputBase-root": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "2px solid #3F51B5",
                    },
                  },
                },
              },
            },
            ...(!disabled && {
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
              },
              "& .MuiInputLabel-root.Mui-disabled": {
                color: "rgba(0, 0, 0, 0.6)",
              },
              "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                color: "rgba(0, 0, 0, 0.54)",
              },
            }),
          }}
        >
          <FormControl error={!!fieldError} fullWidth>
            <TreePickerControlV2
              inputTitle={label}
              title={label}
              dataSource={delayCodes}
              value={value || 0}
              onSelect={(value) => {
                const defaultCategoryId = (
                  Array.isArray(rows) ? rows : []
                ).find((r) => r.DelayCodeId === value)?.DelayCategoryId;
                onChange(value, defaultCategoryId ?? null);
              }}
              selectBranch
              allowSearch
              selectActive={false}
              selectInternal={false}
              disabled={disabled}
            />
            {fieldError && (
              <FormHelperText>{fieldError.message}</FormHelperText>
            )}
          </FormControl>
        </Box>
        <ErrorModal error={getError} onHide={() => setGetError("")} />
      </>
    );
  }
};
